// SPDX-License-Identifier: MIT
// The resume half of the git-as-carrier flow: one observable action that clones the
// project repo, reads its .genesis/ carrier, and hydrates the local stores. Returns a
// structured ResumeResult (step + real message) so the UI can show exactly where it
// stood/failed — NEVER a silent console.warn. Never throws; nothing is half-applied
// (stores are touched only after a successful clone + read).
import { parseGenesisFiles, type GenesisFileMap } from './genesis-state';
import { hydrateLocalStores, type HydrationStores } from './hydrate-stores';

export type ResumeStep = 'cloning' | 'reading' | 'hydrating' | 'done' | 'cancelled' | 'failed';
export interface ResumeResult { ok: boolean; step: ResumeStep; localDir: string | null; message: string; }

export interface ResumeDeps {
  clone: (title: string, cloneUrl: string, cloneToken: string | null) => Promise<{ cancelled: boolean; path: string | null; detail?: string }>;
  readGenesis: (dir: string) => Promise<GenesisFileMap>;
  stores: HydrationStores;
  emit?: (r: ResumeResult) => void;
}

export async function resumeOnThisMachine(
  args: { localProjectId: string; title: string; cloneUrl: string; cloneToken: string | null },
  deps: ResumeDeps,
): Promise<ResumeResult> {
  const report = (r: ResumeResult): ResumeResult => { deps.emit?.(r); return r; };
  report({ ok: false, step: 'cloning', localDir: null, message: 'Clonage du projet…' });
  let out: { cancelled: boolean; path: string | null; detail?: string };
  try {
    out = await deps.clone(args.title, args.cloneUrl, args.cloneToken);
  } catch (e) {
    return report({ ok: false, step: 'failed', localDir: null, message: `Clonage impossible : ${String((e as Error)?.message ?? e)}` });
  }
  if (out.cancelled) return report({ ok: false, step: 'cancelled', localDir: null, message: 'Récupération annulée.' });
  if (!out.path) return report({ ok: false, step: 'failed', localDir: null, message: `Clonage impossible : ${out.detail ?? 'erreur inconnue'}` });
  const localDir = out.path;

  report({ ok: false, step: 'reading', localDir, message: 'Lecture de la fiche…' });
  try {
    const files = await deps.readGenesis(localDir);
    const state = parseGenesisFiles(files);
    report({ ok: false, step: 'hydrating', localDir, message: 'Restauration…' });
    await hydrateLocalStores(state, args.localProjectId, localDir, deps.stores);
  } catch (e) {
    return report({ ok: false, step: 'failed', localDir, message: `Restauration impossible : ${String((e as Error)?.message ?? e)}` });
  }
  return report({ ok: true, step: 'done', localDir, message: 'Projet récupéré sur cette machine.' });
}
